import type React from 'react';
import type { Amount, Fee, SelectedFeeOption } from 'app/types/costAndFees.type';
import { FeeItemContainer, FeeItemContent, FeeItemDisclaimer, FeeItemTitle, PlusMinusInputWrapper } from './styles';
import { useCostsAndFeesContext } from '../../context/CostsAndFeesProvider';
import { useYieldCallback } from '@zillow/yield-callback';
import Checkbox from 'app/shared/core/Checkbox';
import Tooltip from 'app/shared/modules/Tooltip';
import IconMoreInfoActive from 'images/icons/more-info-blue.svg';
import styled from 'styled-components';
import PlusMinusInput from 'app/shared/core/PlusMinusInput';

const TooltipWrapper = styled.span`
  display: inline-block;
  margin-left: 6px;
  position: relative;
  line-height: 16px;
  vertical-align: middle;
  top: -1px;
  z-index: 1;

  /* When tooltip is active, ensure wrapper has high z-index */
  &:has(.Tooltip-tip) {
    z-index: 999998 !important;
  }

  .Tooltip {
    display: inline-flex;
    align-items: center;
  }

  /* Target the tooltip content wrapper that appears before .Tooltip-children */
  .Tooltip > span:not(.Tooltip-children) {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  .Tooltip-tip {
    z-index: 999999 !important;
    pointer-events: auto;
    white-space: normal !important;
    overflow: visible !important;
    background-color: #f6f6fa !important;
    width: auto !important;
    min-width: 150px !important;
    max-width: 220px !important;
  }

  .Tooltip-arrow {
    z-index: 999999 !important;
    left: 3px !important;
    margin-left: 0 !important;
  }

  /* Ensure the tooltip container itself has high z-index */
  .Tooltip > span:first-child {
    z-index: 999999 !important;
  }
`;

interface FeeItemProps {
  fee: Fee;
}

export default function FeeItem({ fee }: FeeItemProps) {
  const { getDisplayAmount, getSelectedOption, updateSelectedOption } = useCostsAndFeesContext();
  const { counter } = fee;
  const optional = counter.min === 0;

  const selectedOption = getSelectedOption(fee);

  const isChecked = !!selectedOption;

  // Determine if the counter input should be shown
  // Show if:
  // - It's optional, checked, and max is > 1 (or null/undefined/Infinity)
  // - It's required (not optional) and max is > 1 (or null/undefined/Infinity)
  const showCounter =
    (optional && isChecked && (counter.max === null || counter.max === undefined || counter.max > 1)) ||
    (!optional && (counter.max === null || counter.max === undefined || counter.max > 1));

  const currentFeeCount = selectedOption?.counterValue ?? (optional ? 1 : counter.min) ?? 0;

  const onCountChange = useYieldCallback((newCount: number) => {
    if (!updateSelectedOption) {
      return;
    }

    // If optional and new count is at or below its minimum (which is 0 for optional fees),
    // this action should result in unchecking the fee.
    if (optional && newCount <= counter.min) {
      if (isChecked) {
        updateSelectedOption(fee, null);
      }
      return;
    }

    // For required fees, or optional fees with a count > counter.min (i.e., > 0).
    // Ensure the count isn't below the fee's defined minimum.
    // (PlusMinusInput's min prop should also enforce this at the input level).
    const effectiveCount = Math.max(newCount, counter.min);

    if (selectedOption?.counterValue === effectiveCount) {
      return;
    }

    const newOptionState: SelectedFeeOption = {
      counterValue: effectiveCount,
      optionSelected: selectedOption?.optionSelected,
    };

    // If this is an optional fee that is checked (isChecked is true from current render)
    // and it has options, but no specific option is part of the current selection state,
    // default to the first available option. This ensures that if an optional fee becomes
    // active (count > 0) and has selectable options, one is chosen.
    // This is mainly a safeguard; onCheckboxChange should handle initial option selection.
    if (optional && isChecked && fee.options && fee.options.length > 0 && !newOptionState.optionSelected) {
      newOptionState.optionSelected = fee.options[0].displayName;
    }

    updateSelectedOption(fee, newOptionState);
  });

  const onCheckboxChange = useYieldCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!updateSelectedOption || !optional) {
      return;
    }

    // Prevent default to avoid any native browser scrolling behavior
    e.preventDefault();

    // Blur the checkbox to prevent focus-related viewport issues on mobile
    if (e.target) {
      e.target.blur();
    }

    let newOptionState: SelectedFeeOption | null = null;

    if (!isChecked) {
      const initialCounter = Math.max(1, counter.min);
      let initialOptionSelected: string | undefined = undefined;

      if (fee.options && fee.options.length > 0) {
        initialOptionSelected = fee.options[0].displayName;
      }

      newOptionState = {
        counterValue: initialCounter,
        optionSelected: initialOptionSelected,
      };
    }
    updateSelectedOption(fee, newOptionState);
  });

  const getFeeTotal = () => {
    const count = selectedOption ? selectedOption.counterValue : counter.min;
    const effectiveCount = isChecked || !optional ? count : 0;

    let feeAmount = fee.amount;
    if (selectedOption?.optionSelected) {
      const selected = fee.options?.find((x) => x.displayName === selectedOption.optionSelected);
      if (selected?.amount) {
        feeAmount = selected.amount;
      }
    }

    let finalFeeAmount: Amount;
    if ('displayText' in feeAmount) {
      finalFeeAmount = { displayText: feeAmount.displayText };
    } else {
      const maxAmount = feeAmount.max ?? feeAmount.min;
      finalFeeAmount = {
        min: feeAmount.min * effectiveCount,
        max: maxAmount * effectiveCount,
      };
    }

    return getDisplayAmount?.(finalFeeAmount);
  };

  const maxInputVal = counter.max === null ? Infinity : counter.max;

  return (
    <FeeItemContainer data-testid="fee-item-container">
      <FeeItemContent>
        <FeeItemTitle>
          {optional && <Checkbox className="" onChange={onCheckboxChange} checked={isChecked} />}
          <span>
            {(() => {
              if (!fee.partnerDisclaimer) {
                return fee.displayName;
              }

              // Split the display name into words
              const words = fee.displayName.split(' ');
              if (words.length === 1) {
                // Single word - keep it with the icon
                return (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {fee.displayName}
                    <TooltipWrapper>
                      <Tooltip tip={fee.partnerDisclaimer} tooltipPosition="above">
                        <img src={IconMoreInfoActive} width="16px" height="16px" alt="More info" />
                      </Tooltip>
                    </TooltipWrapper>
                  </span>
                );
              }

              // Multiple words - keep last word with icon
              const lastWord = words[words.length - 1];
              const restOfText = words.slice(0, -1).join(' ');

              return (
                <>
                  {restOfText}{' '}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {lastWord}
                    <TooltipWrapper>
                      <Tooltip tip={fee.partnerDisclaimer} tooltipPosition="above" width="250px">
                        <img src={IconMoreInfoActive} width="16px" height="16px" alt="More info" />
                      </Tooltip>
                    </TooltipWrapper>
                  </span>
                </>
              );
            })()}
          </span>
        </FeeItemTitle>
        <div data-testid="fee-total" className="fee-total" style={{ flexShrink: 0 }}>
          {getFeeTotal()}
        </div>
      </FeeItemContent>

      {fee.zillowDisclaimers?.length > 0 &&
        fee.zillowDisclaimers.map((disclaimer) => (
          <FeeItemDisclaimer key={disclaimer} hasCheckbox={optional}>
            {disclaimer}
          </FeeItemDisclaimer>
        ))}

      {showCounter && (
        <PlusMinusInputWrapper hasCheckbox={optional}>
          <PlusMinusInput
            defaultValue={currentFeeCount}
            disableKeyboardInput={true}
            max={maxInputVal}
            min={counter.min}
            onChange={(value) => onCountChange(value)}
          />
        </PlusMinusInputWrapper>
      )}
    </FeeItemContainer>
  );
}
